/**
 * AllGenreRadioHero — an immersive "cinema-grade" section showcasing the
 * 24/7 One Voice All Genre Radio.
 *
 * Features:
 *   • 4×6 album-cover wall of the last 24 aired tracks (auto-refresh 30s)
 *   • Covers tilt + glow on hover, ripple when the active slot advances
 *   • Ambient aurora gradient backdrop that pulses to the wall-clock tick
 *   • Animated equalizer bars + floating orbs
 *   • Sticky SyncedRadioPlayer embedded inside a glass-morphism frame
 *   • Gold/cyan split-tone neon border that breathes
 *
 * Design choices (per design guide):
 *   - Dark solid background, zero purple/white gradients
 *   - Left-aligned asymmetric copy, generous spacing
 *   - Motion on every interactive element, staggered reveals
 *   - Transitions bound to opacity/transform only (never `all`)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { SyncedRadioPlayer } from './SyncedRadioPlayer';
import ArtistDrawer from './ArtistDrawer';
import { Radio, Sparkles, Globe2, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_MS = 30_000;
const GRID_SIZE = 24;

// Deterministic fallback color per track (stable across re-renders)
const FALLBACK_PALETTE = [
  '#FFDA33', '#00CCFF', '#FF4D8B', '#00FF88', '#FF7A00',
  '#9B59B6', '#FF4444', '#00E5FF', '#FFB400', '#38D7B2',
];
function colorFor(id) {
  if (!id) return FALLBACK_PALETTE[0];
  let sum = 0;
  for (let i = 0; i < id.length; i++) sum = (sum + id.charCodeAt(i)) % 997;
  return FALLBACK_PALETTE[sum % FALLBACK_PALETTE.length];
}

function CoverTile({ track, index, isCurrent, onOpen }) {
  const color = colorFor(track?.id);
  const artwork = track?.artwork
    ? (track.artwork.startsWith('http')
        ? track.artwork
        : `${BACKEND_URL}/api/artwork/${track.artwork}`)
    : null;

  // Staggered entrance + subtle hover tilt
  const delay = `${index * 40}ms`;

  const clickable = !!(track && track.user_id && onOpen);
  const label = track ? `${track.title} — ${track.artist}` : '';

  const handleActivate = (e) => {
    if (!clickable) return;
    // Stop the event before any ancestor overlay can re-handle it and
    // ensure mobile Safari actually fires onClick after onTouchEnd.
    e.preventDefault();
    e.stopPropagation();
    onOpen(track.user_id);
  };

  return (
    <button
      type="button"
      onClick={handleActivate}
      onTouchEnd={handleActivate}
      disabled={!clickable}
      className={
        "group relative aspect-square overflow-hidden rounded-md border border-white/5 bg-[#0a0a0a] "
        + "opacity-0 translate-y-2 [animation:coverIn_700ms_forwards] "
        + (clickable ? "cursor-pointer hover:border-[#FFDA33]/40 active:border-[#FFDA33]/60" : "cursor-default")
      }
      style={{
        animationDelay: delay,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'rgba(255,218,51,0.25)',
      }}
      data-testid={`all-genre-cover-${index}`}
      title={label}
      aria-label={label}
    >
      {/* Full-tile transparent hit layer — guarantees touch events reach
          the button on iOS/Android even if descendant decorations steal
          them. Sits above artwork but below the LIVE ring / scrim. */}
      <span aria-hidden="true" className="absolute inset-0 z-[1]" />

      {artwork ? (
        <img
          src={artwork}
          alt={track?.title || ''}
          loading="lazy"
          draggable={false}
          className="pointer-events-none select-none w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
      ) : (
        <div
          className="pointer-events-none w-full h-full flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${color}33 0%, #000 75%)` }}
        >
          <Radio size={20} style={{ color, opacity: 0.6 }} />
        </div>
      )}

      {/* dark bottom scrim + title (hover) — pointer-events disabled so
          first tap on iOS isn't consumed by the hover scrim */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/95 via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="text-[9px] font-mono text-white truncate leading-tight">
          {track?.title || '—'}
        </div>
        <div className="text-[8px] font-mono text-white/60 truncate leading-tight">
          {track?.artist || ''}
        </div>
      </div>

      {/* current-track gold ring */}
      {isCurrent && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 rounded-md border-2 border-[#FFDA33] [animation:ringPulse_1800ms_ease-in-out_infinite]" />
          <div className="absolute inset-0 rounded-md bg-[#FFDA33]/10 [animation:ringGlow_2400ms_ease-in-out_infinite]" />
          <div className="absolute top-1 left-1 px-1 py-0.5 rounded bg-[#FFDA33] text-black text-[8px] font-bold font-mono uppercase tracking-widest">
            LIVE
          </div>
        </div>
      )}
    </button>
  );
}

function Equalizer() {
  // 12 bars, randomized heights via CSS animation variables
  const bars = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="flex items-end gap-[3px] h-8" aria-hidden="true">
      {bars.map((i) => (
        <span
          key={i}
          className="w-1 bg-gradient-to-t from-[#FFDA33] to-[#00CCFF] rounded-sm [animation:eqBar_1200ms_ease-in-out_infinite]"
          style={{
            animationDelay: `${(i * 90) % 900}ms`,
            height: `${30 + ((i * 17) % 65)}%`,
          }}
        />
      ))}
    </div>
  );
}

export default function AllGenreRadioHero() {
  const [recents, setRecents] = useState([]);
  const [current, setCurrent] = useState(null);
  const [drawerUser, setDrawerUser] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      try {
        const [nowRes, recentRes] = await Promise.all([
          axios.get(`${API}/radio/now-playing?station=all`),
          axios.get(`${API}/radio/recent-tracks?station=all&limit=${GRID_SIZE}`),
        ]);
        if (!mounted.current) return;
        setCurrent(nowRes.data?.current_track || null);
        setRecents(recentRes.data?.tracks || []);
      } catch {
        /* silent — grid simply stays empty until next poll */
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, []);

  // Pad to 24 slots so the grid always renders a full shape
  const tiles = useMemo(() => {
    const arr = recents.slice(0, GRID_SIZE);
    while (arr.length < GRID_SIZE) arr.push(null);
    return arr;
  }, [recents]);

  return (
    <section
      className="dark-surface relative overflow-hidden border-t border-[#1a1a1a]"
      data-testid="landing-all-genre-radio"
    >
      {/* ===== Ambient backdrop ===== */}
      <div className="absolute inset-0 -z-10 bg-black" aria-hidden="true">
        {/* Aurora orbs */}
        <div className="absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-[#FFDA33]/10 blur-[120px] [animation:drift1_18s_ease-in-out_infinite]" />
        <div className="absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-[#00CCFF]/12 blur-[140px] [animation:drift2_22s_ease-in-out_infinite]" />
        <div className="absolute top-1/3 left-1/2 w-[22rem] h-[22rem] rounded-full bg-[#FF4D8B]/08 blur-[120px] [animation:drift3_26s_ease-in-out_infinite]" />
        {/* fine grid grain */}
        <div
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
          style={{
            backgroundImage:
              'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        {/* ===== Header ===== */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFDA33]/40 bg-[#FFDA33]/10 text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFDA33] mb-4">
              <span className="relative inline-flex">
                <span className="absolute inline-flex h-2 w-2 rounded-full bg-[#FFDA33] opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FFDA33]" />
              </span>
              Now streaming · 24/7 · server-synced
            </div>
            <h2
              className="font-['Chakra_Petch'] uppercase font-bold leading-[0.95] text-4xl md:text-5xl lg:text-6xl"
              data-testid="all-genre-hero-title"
            >
              <span className="block text-white">One Voice</span>
              <span className="block bg-gradient-to-r from-[#FFDA33] via-[#FFE97A] to-[#00CCFF] bg-clip-text text-transparent">
                All Genre Radio
              </span>
            </h2>
            <p className="text-white/70 font-sans text-base md:text-lg mt-4 leading-relaxed">
              One unstoppable stream. Every artist, every genre — AI and human,
              shoulder-to-shoulder. Synced to the same second for every listener,
              everywhere.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-5 text-[11px] font-mono uppercase tracking-widest text-white/60">
              <span className="flex items-center gap-1.5"><Globe2 size={12} className="text-[#00CCFF]" /> Everyone's listening</span>
              <span className="flex items-center gap-1.5"><Sparkles size={12} className="text-[#FFDA33]" /> AI + Human mix</span>
              <span className="flex items-center gap-1.5"><Flame size={12} className="text-[#FF4D8B]" /> Never pauses</span>
            </div>
          </div>

          {/* Equalizer + last-24 counter */}
          <div className="flex items-end gap-4">
            <Equalizer />
            <div className="text-right">
              <div className="text-3xl md:text-4xl font-['Chakra_Petch'] text-white leading-none">
                {recents.length || GRID_SIZE}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-1">
                Recently aired
              </div>
            </div>
          </div>
        </div>

        {/* ===== Player + Cover Wall ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Player (glass frame) */}
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl p-[1px] overflow-hidden [animation:frameGlow_6s_ease-in-out_infinite]"
              style={{
                background:
                  'linear-gradient(120deg, rgba(255,218,51,0.8) 0%, rgba(0,204,255,0.5) 50%, rgba(255,77,139,0.6) 100%)',
              }}
            >
              <div className="relative rounded-2xl bg-black/80 backdrop-blur-xl">
                <SyncedRadioPlayer station="all" locked shareable autoplay />
              </div>
            </div>

            {/* Floating sparkle accents */}
            <div className="relative mt-3 flex gap-2 text-[10px] font-mono text-white/40 uppercase tracking-widest">
              <span className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-[#FFDA33]/30 to-transparent" />
              <span>Tap play once — it runs forever</span>
              <span className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-[#00CCFF]/30 to-transparent" />
            </div>
          </div>

          {/* 4×6 Cover Wall */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-6 gap-1.5 md:gap-2" data-testid="all-genre-cover-wall">
              {tiles.map((t, i) => (
                <CoverTile
                  key={`${t?.id || 'slot'}-${i}`}
                  track={t}
                  index={i}
                  isCurrent={i === 0 && current && t?.id === current?.id}
                  onOpen={(uid) => setDrawerUser(uid)}
                />
              ))}
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-3">
              Last {GRID_SIZE} tracks · tap any cover to preview the artist · auto-refreshes every 30s
            </p>
          </div>
        </div>
      </div>

      {/* Artist side drawer */}
      <ArtistDrawer
        userId={drawerUser}
        open={!!drawerUser}
        onClose={() => setDrawerUser(null)}
      />

      {/* ===== Scoped CSS for keyframes (scoped via component style tag) ===== */}
      <style>{`
        @keyframes coverIn {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ringPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,218,51,0.55); }
          50%      { box-shadow: 0 0 24px 6px rgba(255,218,51,0.25); }
        }
        @keyframes ringGlow {
          0%, 100% { opacity: 0.2; }
          50%      { opacity: 0.55; }
        }
        @keyframes eqBar {
          0%, 100% { transform: scaleY(0.35); }
          40%      { transform: scaleY(1); }
          70%      { transform: scaleY(0.55); }
        }
        @keyframes drift1 {
          0%, 100% { transform: translate(0,0); }
          50%      { transform: translate(40px, 20px); }
        }
        @keyframes drift2 {
          0%, 100% { transform: translate(0,0); }
          50%      { transform: translate(-50px, -30px); }
        }
        @keyframes drift3 {
          0%, 100% { transform: translate(-50%, 0); }
          50%      { transform: translate(calc(-50% + 30px), 40px); }
        }
        @keyframes frameGlow {
          0%, 100% { filter: brightness(1) saturate(1.0); }
          50%      { filter: brightness(1.2) saturate(1.25); }
        }
      `}</style>
    </section>
  );
}
